/**
 * SubKrewe approval, in two layers.
 *
 * - `approved` is the one-time registration decision: is this a real SubKrewe
 *   of the krewe. Regular SubKrewes keep it from one parade year to the next.
 * - Bands (`music === 'band'`) are additionally approved per parade year, in
 *   `approval_by_year[year]`. A registered band is only in a parade once it has
 *   been approved for that parade year; a missing entry means "not decided".
 *
 * Readers that are about *this* parade (lineup, admin badges, reminders) use
 * {@link getParadeApprovalStatus}. Readers that are about whether the SubKrewe
 * exists at all (member browser, Discourse, form rules, public list) keep
 * reading `approved`.
 *
 * Pure functions only — no Firestore, no Nuxt. Synced into `functions/_shared`
 * by scripts/sync-shared-to-functions.mjs.
 */

export type ApprovalStatus = 'Yes' | 'No' | 'Pending'

/** Only actual bands are approved per year; `bring_band` is a regular SubKrewe. */
export const PER_YEAR_APPROVAL_MUSIC_TYPE = 'band'

export interface ApprovalRecordLike {
  status?: string | null
  changed_at?: unknown
  changed_by?: string | null
}

export interface SubKreweApprovalLike {
  music?: string | null
  approved?: string | null
  approval_changed_at?: unknown
  approval_by_year?: Record<string, ApprovalRecordLike | null | undefined> | null
}

/**
 * Which decision an update is making.
 * - `registration`: the one-time decision (the approval task, a captain's
 *   re-application). For a band, approving or denying it also decides the
 *   current parade year.
 * - `parade`: the admin's approval control. Same as `registration` for regular
 *   SubKrewes; for a band it decides the current parade year only.
 */
export type ApprovalScope = 'registration' | 'parade'

export interface ApprovalUpdateOptions {
  scope: ApprovalScope
  paradeYear: number | string
  now: Date
  actorId?: string | null
  reason?: string | null
}

export function isPerYearApprovalSubkrewe(sk: SubKreweApprovalLike | null | undefined): boolean {
  return sk?.music === PER_YEAR_APPROVAL_MUSIC_TYPE
}

/** 'Yes' and 'No' are decisions; anything else (including missing) is 'Pending'. */
export function normalizeApprovalStatus(value: unknown): ApprovalStatus {
  return value === 'Yes' || value === 'No' ? value : 'Pending'
}

export function getBandApprovalRecord(
  sk: SubKreweApprovalLike | null | undefined,
  paradeYear: number | string,
): ApprovalRecordLike | null {
  return sk?.approval_by_year?.[String(paradeYear)] ?? null
}

/**
 * The approval that decides whether a SubKrewe is in the given parade: the
 * registration decision, and for a registered band its decision for that year.
 */
export function getParadeApprovalStatus(
  sk: SubKreweApprovalLike | null | undefined,
  paradeYear: number | string,
): ApprovalStatus {
  const registration = normalizeApprovalStatus(sk?.approved)
  if (!isPerYearApprovalSubkrewe(sk) || registration !== 'Yes') return registration
  return normalizeApprovalStatus(getBandApprovalRecord(sk, paradeYear)?.status)
}

export function isApprovedForParade(
  sk: SubKreweApprovalLike | null | undefined,
  paradeYear: number | string,
): boolean {
  return getParadeApprovalStatus(sk, paradeYear) === 'Yes'
}

/**
 * Firestore field updates for an approval decision. Per-year entries use a
 * dotted path (`approval_by_year.2027`) so other years are left alone, which
 * means the result is meant for `updateDoc`, not `setDoc`. Never contains
 * `undefined` values.
 */
export function buildApprovalUpdate(
  sk: SubKreweApprovalLike,
  status: ApprovalStatus,
  { scope, paradeYear, now, actorId, reason }: ApprovalUpdateOptions,
): Record<string, unknown> {
  const update: Record<string, unknown> = {}
  const actor = actorId || null

  const decideRegistration = () => {
    update.approved = status
    update.approval_changed_at = now
    if (actor) update.approval_changed_by = actor
    if (status === 'Yes') {
      update.approved_at = now
      if (actor) update.approved_by = actor
    }
    else if (status === 'No') {
      if (reason) update.decline_reason = reason
      if (actor) update.declined_by = actor
    }
  }

  const decideYear = () => {
    update[`approval_by_year.${String(paradeYear)}`] = {
      status,
      changed_at: now,
      ...(actor ? { changed_by: actor } : {}),
      ...(status === 'No' && reason ? { reason } : {}),
    }
  }

  if (!isPerYearApprovalSubkrewe(sk)) {
    decideRegistration()
    return update
  }

  if (scope === 'registration') {
    decideRegistration()
    // A captain re-applying (back to Pending) leaves the year undecided.
    if (status !== 'Pending') decideYear()
    return update
  }

  decideYear()
  // Approving a band for the parade also settles a registration still open.
  if (status === 'Yes' && normalizeApprovalStatus(sk.approved) !== 'Yes') decideRegistration()
  return update
}
